import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { v4 } from 'uuid';
import { doc, setDoc, updateDoc } from 'firebase/firestore';

import { DB } from '~/services/firebase';
import getValidationErrors from '~/utils/getValidationErrors';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';
import ICompany from '~/interfaces/ICompany';
import { bff } from '~/services/bff';

interface SettingsCompanyProps {
  openMenu(): void;
}

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const SettingsFidelit: React.FC<SettingsCompanyProps> = ({ openMenu }) => {
  const { user, company, updateCompany } = useAuth();
  const { addToast } = useToast();
  const [data, setData] = useState<ICompany>(company);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setErrors({});

        const schema = Yup.object().shape({
          fidelit: Yup.number().required(),
          fidelit_quantity: Yup.number().required(),
          fidelit_value: Yup.number().required(),
          fidelit_min_value: Yup.number().required(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const parseData = {
          ...data,
          user_id: user.uid,
        };

        const uid = data.uid || v4();

        if (data.uid) {
          await updateDoc(doc(DB, 'companies', uid), parseData);
        } else {
          await setDoc(doc(DB, 'companies', uid), parseData);
        }

        await bff.delete(`/companies/${REACT_APP_FIREBASE_PROJECT}`);

        updateCompany(parseData);

        addToast({
          type: 'success',
          description:
            'As configurações de pagamentos foram salvas com sucesso!',
        });

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
          return;
        }

        addToast({
          type: 'danger',
          description:
            'Infelizmente não foi possível completar a operação. Por favor, Tente novamente.',
        });
      }
    },
    [data, user, addToast, updateCompany],
  );

  return (
    <div className="nk-block">
      <div className="nk-block-head nk-block-between">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title">Fidelidade</h5>
          <div className="nk-block-des">
            <p>Configurar sistema de fidelização de clientes.</p>
          </div>
        </div>
        <div className="nk-block-head-content align-self-start d-lg-none">
          <Link
            to="#menu"
            className="toggle btn btn-icon btn-trigger mt-n1"
            data-target="userAside"
            onClick={openMenu}
          >
            <em className="icon ni ni-menu-alt-r" />
          </Link>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label" htmlFor="default-06">
                Ativar Fidelidade
              </label>
              <div className="form-control-wrap ">
                <div className="form-control-select">
                  <select
                    className="form-control form-control-lg"
                    id="default-06"
                    defaultValue={data.fidelit}
                    onChange={event => {
                      setData({
                        ...data,
                        fidelit: Number(event.target.value),
                      });
                    }}
                  >
                    <option value="0">Não</option>
                    <option value="1">Sim</option>
                  </select>
                  {errors.fidelit && (
                    <span className="invalid">{errors.fidelit}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Quantidade de pedidos</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="10"
                  defaultValue={data.fidelit_quantity}
                  onChange={event => {
                    setData({
                      ...data,
                      fidelit_quantity: Number(event.target.value),
                    });
                  }}
                />
                {errors.fidelit_quantity && (
                  <span className="invalid">{errors.fidelit_quantity}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Ganha R$ a cada pedido</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="R$ 1,00"
                  defaultValue={data.fidelit_value}
                  onChange={event => {
                    setData({
                      ...data,
                      fidelit_value: Number(event.target.value),
                    });
                  }}
                />
                {errors.fidelit_value && (
                  <span className="invalid">{errors.fidelit_value}</span>
                )}
              </div>
            </div>
          </div>

          <div className="col-sm-3">
            <div className="form-group mb-3">
              <label className="form-label">Valor mínimo do pedido</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control form-control-lg"
                  placeholder="R$ 1,00"
                  defaultValue={data.fidelit_min_value}
                  onChange={event => {
                    setData({
                      ...data,
                      fidelit_min_value: Number(event.target.value),
                    });
                  }}
                />
                {errors.fidelit_min_value && (
                  <span className="invalid">{errors.fidelit_min_value}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-group col-sm-12">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
            >
              {loading && (
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              )}
              {!loading && 'Salvar'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsFidelit;
